import * as React from "react"
import { useNavigate } from "react-router-dom"
import { Bell, Check, CheckCheck, CircleAlert, CircleCheck, Inbox, Info, TriangleAlert } from "lucide-react"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"
import { formatNumber, formatTimeAgo } from "@/lib/format"
import { getErrorMessage } from "@/lib/errors"
import { useNotifications } from "@/hooks/useNotifications"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Segmented } from "@/components/ui/segmented"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"
import { notify } from "@/components/ui/notify"

/*
  NotificationsBell — the top-bar bell (02 #9): red dot while something is unread, a popover list with
  "Mark all read", click a notification → marked read and opened (its `url`).

    <NotificationsBell />                         // in Layout's top bar, right side
    <NotificationsBell pollMs={30000} align="end" />

  Data: useNotifications() (GET /api/notifications, /unread-count; POST /mark-read, /mark-all-read). Other
  code that just did something likely to notify the current user can dispatch
  `window.dispatchEvent(new Event('app:notifications-changed'))` to refresh the dot.
  Props: pollMs (60 s), align ('end'), className, testId ('notifications-bell').
*/
const SEVERITY = {
  info: { tone: "navy", icon: Info },
  success: { tone: "green", icon: CircleCheck },
  warning: { tone: "amber", icon: TriangleAlert },
  danger: { tone: "red", icon: CircleAlert },
}

function NotificationRow({ item, onOpen, onMarkRead }) {
  const meta = SEVERITY[item.severity] || SEVERITY.info
  const Icon = meta.icon
  const unread = !item.read_at
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={cn(
          "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none",
          unread && "bg-[color:var(--brand-soft)]"
        )}
        data-testid={`notification-${item.id}`}
        data-unread={unread ? "true" : "false"}
      >
        <span className={cn("ui-icon-tile mt-0.5 h-8 w-8 rounded-lg", toneClass(meta.tone))} aria-hidden="true">
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 pr-6">
          <span className={cn("text-[13.5px] leading-snug text-ink", unread ? "font-bold" : "font-medium")}>
            {item.title}
          </span>
          {item.body ? <span className="line-clamp-2 text-[12.5px] text-ink-2">{item.body}</span> : null}
          <span className="text-[12px] text-ink-3">
            {[item.actor_name, formatTimeAgo(item.created_at)].filter(Boolean).join(" · ")}
          </span>
        </span>
        {unread ? (
          <>
            <span className="absolute right-4 top-4 h-2 w-2 rounded-full bg-status-red" aria-hidden="true" />
            <span className="sr-only">Unread.</span>
          </>
        ) : null}
      </button>
      {unread ? (
        <button
          type="button"
          onClick={() => onMarkRead(item)}
          className="absolute bottom-2.5 right-3 grid h-7 w-7 place-items-center rounded-lg text-ink-3 opacity-100 transition-opacity hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:focus-visible:opacity-100 [@media(hover:hover)]:group-hover:opacity-100"
          aria-label={`Mark “${item.title}” as read`}
          title="Mark as read"
          data-testid={`notification-mark-${item.id}`}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </li>
  )
}

function NotificationsBell({ pollMs = 60_000, align = "end", className, testId = "notifications-bell" }) {
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)
  const [view, setView] = React.useState("all")
  const {
    unreadCount, items, loading, loadingMore, loaded, error, hasMore, loadList, loadMore, markRead, markAllRead,
  } = useNotifications({ pollMs })

  const onOpenChange = (next) => {
    setOpen(next)
    if (next) loadList()
  }

  const safely = async (action, failure) => {
    try {
      await action()
    } catch (err) {
      notify.error(failure, { description: getErrorMessage(err) })
    }
  }

  const openItem = (item) => {
    if (!item.read_at) safely(() => markRead([item.id]), "Could not mark the notification as read")
    if (item.url) {
      setOpen(false)
      navigate(item.url)
    }
  }

  const visible = view === "unread" ? items.filter((item) => !item.read_at) : items
  const countText = unreadCount > 99 ? "99+" : formatNumber(unreadCount, { maxDecimals: 0 })

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative grid h-9 w-9 place-items-center rounded-control text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className
          )}
          aria-label={unreadCount ? `Notifications, ${countText} unread` : "Notifications"}
          data-testid={testId}
        >
          <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
          {unreadCount ? (
            <span
              className="absolute right-[7px] top-[7px] h-2.5 w-2.5 rounded-full border-2 border-[color:var(--app-header-background)] bg-status-red"
              aria-hidden="true"
              data-testid={`${testId}-dot`}
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={8}
        collisionPadding={12}
        className="flex max-h-[min(560px,calc(100dvh-96px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-card border-line bg-surface p-0 text-ink shadow-overlay"
        data-testid={`${testId}-panel`}
        aria-label="Notifications"
      >
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <h2 className="font-heading text-[15px] font-bold">Notifications</h2>
          {unreadCount ? (
            <span className="rounded-full bg-status-red-bg px-2 py-0.5 text-[11.5px] font-bold text-status-red-ink">
              {countText} new
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="ml-auto"
            disabled={!unreadCount}
            onClick={() => safely(markAllRead, "Could not mark all as read")}
            data-testid={`${testId}-mark-all`}
          >
            <CheckCheck aria-hidden="true" />Mark all read
          </Button>
        </div>
        <div className="border-b border-line-2 px-4 py-2">
          <Segmented
            size="sm"
            ariaLabel="Show"
            value={view}
            onChange={setView}
            options={[{ key: "all", label: "All" }, { key: "unread", label: "Unread", count: unreadCount }]}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" aria-busy={loading || undefined}>
          {loading && !loaded ? (
            <div className="space-y-3 p-4" data-testid={`${testId}-loading`}>
              {[0, 1, 2].map((key) => <Skeleton key={key} className="h-12 w-full rounded-lg" />)}
            </div>
          ) : error && !items.length ? (
            <EmptyState
              layout="inline"
              tone="red"
              icon={CircleAlert}
              titleAs="p"
              title="Couldn’t load notifications"
              text={getErrorMessage(error)}
              action={<Button type="button" size="sm" variant="outline" onClick={loadList}>Try again</Button>}
            />
          ) : visible.length ? (
            <ul className="divide-y divide-line-2" aria-label={view === "unread" ? "Unread notifications" : "Notifications"}>
              {visible.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onOpen={openItem}
                  onMarkRead={(row) => safely(() => markRead([row.id]), "Could not mark the notification as read")}
                />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Inbox}
              titleAs="p"
              title={view === "unread" ? "No unread notifications" : "You’re all caught up"}
              text="Approvals, assignments and reminders for you appear here."
            />
          )}
        </div>
        {hasMore && view === "all" ? (
          <div className="border-t border-line px-4 py-2">
            <Button type="button" variant="ghost" size="sm" className="w-full" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Show older"}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

export { NotificationsBell }
